import { __assign } from "tslib";
import { elapsed, generateUUID, monitor, ONE_MINUTE, throttle, clocksNow, preferredClock, clocksOrigin, } from '@datadog/browser-core';
import { ViewLoadingType } from '../../../rawRumEvent.types';
import { LifeCycleEventType } from '../../lifeCycle';
import { trackInitialViewTimings } from './trackInitialViewTimings';
import { trackLocationChanges, areDifferentLocation } from './trackLocationChanges';
import { trackViewMetrics } from './trackViewMetrics';
export var THROTTLE_VIEW_UPDATE_PERIOD = 3000;
export var SESSION_KEEP_ALIVE_INTERVAL = 5 * ONE_MINUTE;
export function trackViews(location, lifeCycle) {
    var isRecording = false;
    // eslint-disable-next-line prefer-const
    var _a = trackInitialView(), stopInitialViewTracking = _a.stop, currentView = _a.initialView;
    var stopLocationChangesTracking = trackLocationChanges(function () {
        if (areDifferentLocation(currentView.getLocation(), location)) {
            // Renew view on location changes
            currentView.end();
            currentView.triggerUpdate();
            currentView = trackViewChange();
            return;
        }
        currentView.updateLocation(location);
        currentView.triggerUpdate();
    }).stop;
    // Renew view on session renewal
    lifeCycle.subscribe(LifeCycleEventType.SESSION_RENEWED, function () {
        // do not trigger view update to avoid wrong data
        currentView.end();
        currentView = trackViewChange();
    });
    // End the current view on page unload
    lifeCycle.subscribe(LifeCycleEventType.BEFORE_UNLOAD, function () {
        currentView.end();
        currentView.triggerUpdate();
    });
    lifeCycle.subscribe(LifeCycleEventType.RECORD_STARTED, function () {
        isRecording = true;
        currentView.updateHasReplay(true);
    });
    lifeCycle.subscribe(LifeCycleEventType.RECORD_STOPPED, function () {
        isRecording = false;
    });
    // Session keep alive
    var keepAliveInterval = window.setInterval(monitor(function () {
        currentView.triggerUpdate();
    }), SESSION_KEEP_ALIVE_INTERVAL);
    function trackInitialView() {
        var initialView = newView(lifeCycle, location, isRecording, ViewLoadingType.INITIAL_LOAD, document.referrer, clocksOrigin());
        var stop = trackInitialViewTimings(lifeCycle, function (timings) {
            initialView.updateTimings(timings);
            initialView.scheduleUpdate();
        }).stop;
        return { initialView: initialView, stop: stop };
    }
    function trackViewChange() {
        return newView(lifeCycle, location, isRecording, ViewLoadingType.ROUTE_CHANGE, currentView.url);
    }
    return {
        addTiming: function (name, endClocks) {
            if (endClocks === void 0) { endClocks = clocksNow(); }
            currentView.addTiming(name, endClocks);
            currentView.triggerUpdate();
        },
        stop: function () {
            stopInitialViewTracking();
            stopLocationChangesTracking();
            currentView.end();
            clearInterval(keepAliveInterval);
        },
    };
}
function newView(lifeCycle, initialLocation, initialHasReplay, loadingType, referrer, startClocks, name) {
    if (startClocks === void 0) { startClocks = clocksNow(); }
    // Setup initial values
    var id = generateUUID();
    var timings = {};
    var customTimings = {};
    var documentVersion = 0;
    var endClocks;
    var location = __assign({}, initialLocation);
    var hasReplay = initialHasReplay;
    lifeCycle.notify(LifeCycleEventType.VIEW_CREATED, { id: id, startClocks: startClocks, location: location, referrer: referrer });
    // Update the view every time the measures are changing
    var _a = throttle(monitor(triggerViewUpdate), THROTTLE_VIEW_UPDATE_PERIOD, {
        leading: false,
    }), scheduleViewUpdate = _a.throttled, cancelScheduleViewUpdate = _a.cancel;
    var _b = trackViewMetrics(lifeCycle, scheduleViewUpdate, loadingType), setLoadEvent = _b.setLoadEvent, stopViewMetricsTracking = _b.stop, viewMetrics = _b.viewMetrics;
    // Initial view update
    triggerViewUpdate();
    function triggerViewUpdate() {
        documentVersion += 1;
        lifeCycle.notify(LifeCycleEventType.VIEW_UPDATED, __assign(__assign({}, viewMetrics), { customTimings: customTimings,
            documentVersion: documentVersion,
            id: id,
            name: name,
            loadingType: loadingType,
            location: location,
            hasReplay: hasReplay,
            referrer: referrer,
            startClocks: startClocks,
            timings: timings, duration: elapsed(preferredClock(startClocks), preferredClock(endClocks === undefined ? clocksNow() : endClocks)), isActive: endClocks === undefined }));
    }
    return {
        scheduleUpdate: scheduleViewUpdate,
        end: function () {
            endClocks = clocksNow();
            stopViewMetricsTracking();
            lifeCycle.notify(LifeCycleEventType.VIEW_ENDED, { endClocks: endClocks });
        },
        getLocation: function () {
            return location;
        },
        triggerUpdate: function () {
            // cancel any pending view updates execution
            cancelScheduleViewUpdate();
            triggerViewUpdate();
        },
        updateTimings: function (newTimings) {
            timings = newTimings;
            if (newTimings.loadEvent !== undefined) {
                setLoadEvent(newTimings.loadEvent);
            }
        },
        addTiming: function (name, endClocks) {
            customTimings[sanitizeTiming(name)] = elapsed(preferredClock(startClocks), preferredClock(endClocks));
        },
        updateLocation: function (newLocation) {
            location = __assign({}, newLocation);
        },
        updateHasReplay: function (newHasReplay) {
            hasReplay = newHasReplay;
        },
        get url() {
            return location.href;
        },
    };
}
/**
 * Timing name is used as facet path that must contain only letters, digits, or the characters - _ . @ $
 */
function sanitizeTiming(name) {
    var sanitized = name.replace(/[^a-zA-Z0-9-_.@$]/g, '_');
    if (sanitized !== name) {
        console.warn("Invalid timing name: " + name + ", sanitized to: " + sanitized);
    }
    return sanitized;
}
//# sourceMappingURL=trackViews.js.map